import { WizValidatorFn, WizValidationErrors } from './../../services/validators/wiz-validators';
import { IWIZError } from './../../interfaces/iwiz-error';
import { WizComponentManager } from './../../services/core/component/wiz-component.manager';
import {
  Input, ElementRef, OnInit, Renderer2, AfterViewInit, OnDestroy,
  ViewContainerRef, InjectionToken, ChangeDetectorRef, Directive, HostBinding, HostListener
} from '@angular/core';
import { IWIZFocusable } from '../../interfaces/iwiz-focusable';
import { WizFocusManager } from '../../services/core/focus/wiz-focus-manager';
import { IWIZComponent} from '../../interfaces/iwiz-component';
import { WIZ_EVENT_TYPE, WizMathUtils } from '@wizeoo/akm-core-lib';
import { WizEvent } from '../../events/wiz-event';
import { WizEventManager } from '../../events/wiz-event.manager';
import { WizTranslableProperty } from '../../services/translation/wiz-translatable-property.decorator'; 

/**
 * Injection token for any Component that extends BaseCompoent.
 * This allows component instance injection of any wizBaseComponent in the constructor of any Directive
 *
 * usage example for component declaration :
 * @Component({
 *  ...
 *   providers: [
 *     { provide: BASE_COMPONENT, useExisting: forwardRef( () => WizTextInput )},
 *     { provide: WizBaseComponent, useExisting: forwardRef( () => WizTextInput )}
 *   ]
 * }
 *
 * Usage example in DI constructor:
 * constructor (@Inject(BASE_COMPONENT) @Self() public sourceComponent: WizBaseComponent) { }
 */
export const BASE_COMPONENT = new InjectionToken('BASE_COMPONENT');

/* WARNING
  Style properties modifications are made by directives in the /lib/components/layouts folder
  It is possible to directly modify these properties via these attributes
  <div width="100%" height="100%" wizStyleClass="align-center" wizStyle="{background-color: red]">
*/
/** Base class for all wiz' components */
@Directive()
export abstract class WizBaseComponent implements IWIZComponent, IWIZFocusable, IWIZError, OnInit, AfterViewInit, OnDestroy {

  /**  ajout de l'écouteur sur change pour dispatch d'un modelUpdate, uniquement si le composant est manageModel = true (default) */
  @HostListener('WIZ_CHANGE',['$event'])  dispatchModelUpdate(event: WizEvent) {
    // basicComponent pour neutraliser les collectors. Leur cycle est géré dans wiz-base-collector
    if(this.manageModel && this.basicComponent ){
      WizEventManager.dispatchEvent(this.elementRef, new WizEvent(WIZ_EVENT_TYPE.MODEL_UPDATE, this, this._wizValue));
    }
  }

  @HostBinding('attr.wiz-disabled')
  public get attrIsDisabled(): string {
    return `${this._wizDisabled}`;
  }

  /**
   * Ajout de la propriété flex-shrink = 0;// LMA Non. C'est con. Ne permet plus le redimensionnement
   * Cela évite l'écrasement du composant dans le cadre d'une zoom navigateur
   */
  @HostBinding('style.flex-shrink') get flexshrink(): number {
    return this.getFlexShrink();
  }

  public getFlexShrink(): number {
    return 1;
  }

  @Input() wizTabIndex = 0;

  /** gestion de la visibilité */
  @HostBinding('class.invisible-class') get visibility() {
    return this._visible === false;
  }
  private _visible: boolean = true;
  public get visible(): boolean {
    return this._visible;
  }
  @Input() public set visible(value: boolean) {
    this._visible = value;
    this.cdref.markForCheck();
  }

  /**
   * Component mandatory : véritablement obligatoire
   */
  _wizMandatory: boolean = false;
  @Input()
  public set wizMandatory(v: boolean) {
    this._wizMandatory = v;
    this.cdref.markForCheck();
  }
  public get wizMandatory(): boolean {
    return this._wizMandatory;
  }

  /** type de prise de focus */
  _wizFocusMode: 'STOP' | 'ROLLBACK' | 'PASS' = 'PASS';
  @Input()
  public set wizFocusMode(v: 'STOP' | 'ROLLBACK' | 'PASS' ) {
    this._wizFocusMode = v;
    this.cdref.markForCheck();
  }
  public get wizFocusMode(): 'STOP' | 'ROLLBACK' | 'PASS' {
    return this._wizFocusMode;
  }
  // accesseurs mode Java pour override dans les classes héritées
  public setWizFocusMode(v: 'STOP' | 'ROLLBACK' | 'PASS') {
    this._wizFocusMode = v;
    this.cdref.markForCheck();
  }
  public getWizFocusMode(): 'STOP' | 'ROLLBACK' | 'PASS' {
    return this._wizFocusMode;
  }


  /** Component disabled */
  protected _wizDisabled: boolean = false;
  @Input()
  public set wizDisabled(v: boolean) {
    this.setWizDisabled(v);
  }
  public get wizDisabled(): boolean {
    return this.getWizDisabled();
  }

  // accesseurs mode Java pour override dans les classes héritées
  public setWizDisabled(v: boolean) {
    this._wizDisabled = v;
    this.checkStyle(); // refresh du style
    this.cdref.markForCheck();
  }
  public getWizDisabled(): boolean {
    return this._wizDisabled;
  }

  /**
   * Fix size to hint . Used to align components
   */
  _wizKeepShowHint: boolean = false;
  @Input()
  public set wizKeepShowHint(v: boolean) {
    this._wizKeepShowHint = v;
    this.cdref.markForCheck();
  }
  public get wizKeepShowHint(): boolean {
    return this._wizKeepShowHint;
  }

  /**
   * Component UID
   */
  private _entityUID: string;

  /**
   * Component label
   */
  protected _wizLabel: string;
  @Input('wizLabel')
  public set wizLabel(pLabel: string) {
    this._wizLabel = pLabel;
    this.cdref.markForCheck();
  }
  public get wizLabel(): string {
    return this._wizLabel;
  }


  public get entityUID(): string {
    return this._entityUID;
  }

  /**
   * id
   * return id of elementRef.nativeElement
   */
  public get id(): string {
    return this.elementRef.nativeElement.id;
  }
  public set id(value: string) {
    this.elementRef.nativeElement.id = value;
  }

  constructor(protected cdref: ChangeDetectorRef, public elementRef: ElementRef, public  renderer: Renderer2) {
    this._entityUID = WizMathUtils.createUUID();
  }

  ngAfterViewInit() {
  }

  ngOnInit() {
    // listening of the mousedown event on the component's dom
    // TODO attach the event on the component's dom
    // this.baseCompoDom.nativeElement.addEvenetListener('mousedown', e => this.wizGetFocus());
    WizComponentManager.instance.registerComponent(this, this.elementRef);
  }

  ngOnDestroy(): void {
    WizComponentManager.instance.unregisterComponent(this, this.elementRef);
  }

  /**
   * @inheritdoc
   */
  wizGetFocus() {
    // console.log('wizGetFocus',this);
    WizFocusManager.getInstance().wizRegisterFocus(this);
    WizEventManager.dispatchEvent(this.elementRef, new WizEvent(WIZ_EVENT_TYPE.FOCUS_IN, this));
  }

  /**
   * @inheritdoc
   */
  public wizFocus(): void {
    // console.log('focus');
  }

  /**
   * donne le focus à l'input depuis FocusManager
   * @param value valeur à injecter
   */
  public wizFocusInjector(value: string): void {
  }


  /**
   * @inheritdoc
   */
  wizLoseFocus() {
    // console.log('wizLoseFocus',this);
    WizFocusManager.getInstance().wizUnregisterFocus(this);
    WizEventManager.dispatchEvent(this.elementRef, new WizEvent(WIZ_EVENT_TYPE.FOCUS_OUT, this));
  }

  /**
   * contrat d'interface IwizError: traitement complémentaire pour rollBack
   * sur focusOut. Le retour à la dernière bonne conf est déjà géré par ErrorManager
   */
  public wizRollback(): void {
    // console.log('rollback');
  }

  /**
   * @inheritdoc
   */
  getErrorVCRef(): ViewContainerRef {
    return null;
  }

  /** application du style effectif (disabled, autres...) */
  checkStyle() {
    // déclenché par des méthodes internes (wizDisabled...)
    // console.log('disa');
  }

  /**
   * @inheritdoc
   */
  getValidators(): WizValidatorFn[] { return []; }

  /**
   * @inheritdoc
   */
  prepareForCheck():void {};

  /**
   * @inheritdoc
   */
  onWizValidation(pErrors: WizValidationErrors, wizShowErrors: boolean): void {
    // We do nothing in the base component
  }

  /** valeur normalisée du composant */
  protected _wizValue: any = null;
  public setWizValue(value : any) {
    this._wizValue = value;
  }
  public getWizValue():any {
    return this._wizValue;
  }

  @Input()
  public set wizValue(pValue: any) {
    this.setWizValue(pValue);
  }
  public get wizValue(): any {
    return this.getWizValue();
  }

  /** dernière bonne wizValue connue pour le composant */
  protected _lastGoodValue: any = null;
  public setLastGoodValue(value : any) {
    this._lastGoodValue = value;
  }
  public getLastGoodValue():any {
    return this._lastGoodValue;
  }

  /**
   * le composant est BASIC : ce n'est PAS un WizBaseCollector
   */
  private _basicComponent: boolean = true;
  @Input() public set basicComponent(v: boolean) {
    this._basicComponent = v;
    this.cdref.markForCheck();
  }
  public get basicComponent(): boolean {
    return this._basicComponent;
  }

  /**
   * le composant possède une donnée METIER. true par défaut
   * permet de dispatcher l'event WIZ_EVENT.MODEL_UPDATE en plus du change
   * Si false, pas de model_update (juste le change)
   */
  private _manageModel: boolean = true;
  @Input() public set manageModel(v: boolean) {
    this._manageModel = v;
    this.cdref.markForCheck();
  }
  public get manageModel(): boolean {
    return this._manageModel;
  }

  /**
   * Optionnel. Le nom de la propriété métier (prop.subprop.. autorisé)
   * permet un traitement auto des résultats en liaison avec _manageMode
   */
  private _modelProperty: string = null;
  @Input() public set modelProperty(v: string) {
    this._modelProperty = v;
    this.cdref.markForCheck();
  }
  public get modelProperty(): string {
    return this._modelProperty;
  }

  /**
   * Forcer le texte du tooltip
   * S'il est renseigné, on l'affiche dans le tooltip
   * Sinon, si le texte est en partie visible, il prend la valeur du translatedLabel
   */
  public translatedTooltip: string;
  @WizTranslableProperty('translatedTooltip')
  @Input() public set tooltipText(v: string) {
    this._tooltipText =v;
  }
  public get tooltipText(): string {
    return this._tooltipText;
  }
  protected _tooltipText: string;

  @Input()
  undisableable: boolean = false;
}
